import Swal from 'sweetalert2';
import DetectRTC from 'detectrtc';
import { Device } from 'mediasoup-client';

import {
  adaptAspectRatio,
  getDataTimeString,
  getNotify,
  isHideALLVideosActive,
  shareRoom,
  playSound,
  getMediaStreamSource,
  applyNoiseCancellation,
  forceCameraRelease,
  applyNoiseCancelledStream,
  handleCameraError,
  generatePeerInfo,
  isWebView,
} from '@src/helpers';

import {
  convertToMilliseconds,
  getMapKeyByValue,
  getEventTime,
  openURL,
  speechText,
  isSamsungDevice,
} from '@src/utils';

import {
  getMediaSoupVideoConstraints,
  getUserMediaWithFallback as getUserMediaWithFallbackUtil,
} from '@src/utils/videoConstraints.utils';

import { MixedAudioRecorder } from './MixedAudioRecorder';
import SocketService from './SocketService';
import { fetchMagicPenAnnotations } from '@src/components/MagicPen/services';
import { exitRoomService } from './exitRoomService';
import { cleanProducers } from './producerService';
import { LocalStorage } from './LocalStorage';
import { audioProcessor } from './AudioProcessorService';

import {
  usePeersStore,
  useVideoStreamStore,
  useRoomActionsStore,
  useProducersStore,
  useRoomStore,
  useRecordingStore,
  useConsumersStore,
  useAudioStreamStore,
  useMediaSettingsStore,
  useNotificationStore,
  useRoomConfigStore,
  useMagicPenStore,
  useVirtualBackgroundStore,
} from '@src/stores';
import { hydrateSharedCalculator } from '@src/services/calculatorHydrator';

import { EMeetingRoutes, INFO_MODAL_ICONS } from '@src/constants';
import { ECommonSocketKeys, EMediaType, EPeerPermission } from '@src/types';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge';

const image = {
  about: '../v.png',
  avatar: '../v.png',
  audio: '../images/audio.gif',
  poster: '../images/loader.gif',
  rec: '../images/rec.png',
  recording: '../images/recording.png',
  delete: '../images/delete.png',
  locked: '../images/locked.png',
  mute: '../images/mute.png',
  hide: '../images/hide.png',
  stop: '../images/stop.png',
  unmute: '../images/unmute.png',
  unhide: '../images/unhide.png',
  start: '../images/start.png',
  users: '../images/participants.png',
  user: '../images/participant.png',
  username: '../images/user.png',
  videoShare: '../images/video-share.png',
  message: '../images/message.png',
  share: '../images/share.png',
  exit: '../images/exit.png',
  feedback: '../images/feedback.png',
  lobby: '../images/lobby.png',
  email: '../images/email.png',
  chatgpt: '../images/chatgpt.png',
  all: '../images/all.png',
  forbidden: '../images/forbidden.png',
  broadcasting: '../images/broadcasting.png',
  geolocation: '../images/geolocation.png',
  network: '../images/network.gif',
  save: '../images/save.png',
};

const _EVENTS = {
  openRoom: 'openRoom',
  startRec: 'startRec',
  pauseRec: 'pauseRec',
  resumeRec: 'resumeRec',
  stopRec: 'stopRec',
  raiseHand: 'raiseHand',
  lowerHand: 'lowerHand',
  startVideo: 'startVideo',
  pauseVideo: 'pauseVideo',
  resumeVideo: 'resumeVideo',
  stopVideo: 'stopVideo',
  startAudio: 'startAudio',
  pauseAudio: 'pauseAudio',
  resumeAudio: 'resumeAudio',
  stopAudio: 'stopAudio',
  startScreen: 'startScreen',
  pauseScreen: 'pauseScreen',
  resumeScreen: 'resumeScreen',
  stopScreen: 'stopScreen',
  roomLock: 'roomLock',
  roomUnlock: 'roomUnlock',
};

// Enums

// Recording
let recordedBlobs = [];

const lS = new LocalStorage();
const localStorageSettings = lS.getLocalStorageSettings() || lS.SFU_SETTINGS;
class RoomClient {
  constructor(
    room_id,
    peer_name,
    peer_uuid,
    // callbacks
    wbUpdate, //TODO: convert it to state
    roomIsReady
  ) {
    this.room_id = room_id;
    this.peer_id = SocketService.getSocketId();
    this.peer_name = peer_name;
    this.peer_uuid = peer_uuid;
    this.isAudioEventListenerActive = true;

    // Camera processing flag to prevent race conditions
    this.isCamProcessing = false;

    // Atomic camera operation lock with promise queue
    this.cameraOperationLock = null;
    // Device type
    this.isMobileDevice = DetectRTC.isMobileDevice;

    // callbacks
    this.wbUpdate = wbUpdate;

    this.participantsCount = usePeersStore.getState().peers?.size;

    // Chat messages

    this.consumerTransport = null;
    this.device = null;

    this.camVideo = false;
    this.camera = 'user';

    // Recording
    this._isRecording = false;
    this.mediaRecorder = null;
    this.audioRecorder = null;
    this.recScreenStream = null;

    this._isReconnecting = false;
    this.eventListeners = new Map();

    this.debug = false;
    this.debug ? window.localStorage.setItem('debug', 'mediasoup*') : window.localStorage.removeItem('debug');

    Object.keys(_EVENTS).forEach((evt) => {
      this.eventListeners.set(evt, []);
    });

    // Set global reference for coordination between components
    if (typeof window !== 'undefined') {
      window.roomClient = this;
    }

    // ####################################################
    // CREATE ROOM AND JOIN
    // ####################################################

    this.createRoom(this.room_id).then(async () => {
      this.initSockets();
      roomIsReady();
    });
  }

  // ####################################################
  // GET STARTED
  // ####################################################

  async createRoom(room_id) {
    const roomTitle = useRoomStore.getState().roomTitle;

    roomTitle &&
      (await SocketService.request('createRoom', {
        room_id,
        room_title: roomTitle,
      }).catch((err) => {
        console.log('Create room:', err);
      }));

    const data = {
      room_id: this.room_id,
      peer_info:
        usePeersStore.getState().getMyPeerInfo() ??
        generatePeerInfo({ peerName: this.peer_name, peerImage: usePeersStore.getState().avatarUrl || '' }),
    };
    await this.join(data);
  }
  _scheduleMeetingEndSoon({ startTime, duration, unit = 'minutes', warnLeadMs = convertToMilliseconds(5, 'minutes') }) {
    if (this._meetingEndSoonTimer) {
      clearTimeout(this._meetingEndSoonTimer);
      this._meetingEndSoonTimer = null;
    }

    const timers = getEventTime({ startTime, duration, unit, warnLeadMs });
    if (!timers) return;

    const { endAt, warnAt } = timers;
    const now = Date.now();

    const fire = () => {
      const { appendNotification } = useNotificationStore.getState();
      appendNotification({
        title: 'Meeting Ending Soon',
        text: 'This meeting will end in 5 mins.',
        source: 'meeting-end-soon',
        id: 'meeting-end-soon',
      });
    };

    if (now >= endAt) return;

    if (now >= warnAt) {
      fire();
    } else {
      this._meetingEndSoonTimer = window.setTimeout(fire, Math.max(warnAt - now, 0));
    }
  }

  async join(data) {
    SocketService.request('join', data)
      .then(async (room) => {
        const newPeers = typeof room.peers === 'string' ? JSON.parse(room.peers) : room.peers;
        usePeersStore.getState().initPeers(new Map(newPeers));

        // Check if the current peer has MAGIC_PEN_ALLOW_ANNOTATE permission
        const mySocketId = SocketService.getSocketId();
        const myPeerData = newPeers.find(([peerId]) => peerId === mySocketId);
        if (myPeerData && myPeerData[1]?.permissions?.includes(EPeerPermission.MAGIC_PEN_ALLOW_ANNOTATE)) {
          useRoomStore.getState().setHasMagicPenAnnotatePermission(true);
        }

        useRoomStore.setState({ isJoinedRoom: true });

        sendEventToWebView({ eventType: EWebViewEvents.MEETING_STARTED });

        if (room.hasAnnotations) {
          await fetchMagicPenAnnotations();
        }
        room.roomTitle && useRoomStore.getState().setRoomTitle(room.roomTitle);
        room.isHostUser && usePeersStore.setState({ isHostUser: true });

        //Fetch room settings for New Joiners from 'join' socket
        room?.roomConfig &&
          useRoomConfigStore.setState({
            mainConfig: room.roomConfig.main,
            chatConfig: room.roomConfig.chat,
            pollConfig: room.roomConfig.poll,
            settingsConfig: room.roomConfig.settings,
            questionsConfig: room.roomConfig.questions,
            participantsListConfig: room.roomConfig.participantsList,
            magicPenConfig: room.roomConfig.magicPen,
            calculatorConfig: room.roomConfig.calculator,
          });

        hydrateSharedCalculator(room?.calculatorDetails, this.peer_id);
        const perms = room?.roomPermissions || data?.roomPermissions || {};
        const startTime = room?.created_at ?? data?.created_at ?? Date.now();
        const duration = perms?.sessions_duration ?? 0;
        const unit = perms?.sessions_duration_unit ?? 'minutes';

        this._scheduleMeetingEndSoon({ startTime, duration, unit });
        await this.joinAllowed(room);
      })
      .catch((error) => {
        console.error('Join error:', error);
        if (error.includes('Max participants')) {
          openURL(EMeetingRoutes.ROOM_FULL);
          return;
        }
        if (error.includes('Something went')) {
          openURL(EMeetingRoutes.END);
          return;
        }
        if (error === 'isBanned') {
          openURL(EMeetingRoutes.RESTRICTED_ACCESS);
          return;
        }
        sendEventToWebView({ eventType: EWebViewEvents.MEETING_ENDED });

        if (error.includes('already exists')) {
          useRoomStore.getState().setJoinFormErrors({ username: 'This name is already in use. Please choose another' });
        } else {
          Swal.fire({
            background: localStorageSettings.swalBackground,
            position: 'center',
            html: `
              <p>${error}</p>
              <p>You will be redirected</p>
            `,
            confirmButtonText: 'Got it',
            showClass: { popup: 'animate__animated animate__fadeInDown' },
            hideClass: { popup: 'animate__animated animate__fadeOutUp' },
          }).then((result) => {
            window.location.replace(process.env.REACT_APP_SSO_URL);
            sendEventToWebView({ eventType: EWebViewEvents.CLOSE });
          });
        }
      });
  }

  async joinAllowed(room) {
    await this.handleRoomInfo(room);
    const routerRtpCapabilities = await SocketService.request('getRouterRtpCapabilities');
    routerRtpCapabilities.headerExtensions = routerRtpCapabilities.headerExtensions.filter(
      (ext) => ext.uri !== 'urn:3gpp:video-orientation'
    );
    this.device = await this.loadDevice(routerRtpCapabilities);
    await this.initTransports(this.device);
    this.initTransportsSockets();
    // ###################################
    SocketService.emit('getProducers');
    // ###################################
    await this.startLocalMedia();
  }

  async handleRoomInfo(room) {
    // redirect = room.redirect;
    this.participantsCount = usePeersStore.getState().peers?.size;
    // ME
    window.localStorage.isReconnected = false;

    // There is polls
    if (room.thereIsPolls) {
      SocketService.emit('updatePoll');
    }

    // PARTICIPANTS
    for (let peer of Array.from(usePeersStore.getState().peers.keys()).filter((id) => id !== this.peer_id)) {
      let peer_info = usePeersStore.getState().peers.get(peer).peer_info;

      const { peer_video, peer_recording } = peer_info;

      if (!peer_video) {
        this.setVideoOff(peer_info, true);
      }

      if (peer_recording) {
        useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: true });
        // Defer speechText to avoid blocking the join flow in WebView
        // This is critical for WebView where speech synthesis can cause timing issues
        if (isWebView()) {
          try {
            speechText('Start Meeting recording');
          } catch (e) {
            console.warn('[WebView] speechText failed:', e);
          }
        } else {
          speechText('Start Meeting recording');
        }
      }
    }

    this.refreshParticipantsCount();

    // notify && this.participantsCount == 1 ? shareRoom() : playSound('joined');
    if (getNotify() && this.participantsCount === 1) {
      shareRoom(this, false);
    }
  }

  async loadDevice(routerRtpCapabilities) {
    let device;
    try {
      device = new Device();
    } catch (error) {
      console.error(error);
    }
    await device.load({
      routerRtpCapabilities,
    });
    return device;
  }

  // ####################################################
  // TRANSPORTS
  // ####################################################

  async initTransports(device) {
    // ####################################################
    // PRODUCER TRANSPORT
    // ####################################################

    const producerTransportData = await SocketService.request('createWebRtcTransport', {
      forceTcp: false,
      rtpCapabilities: device.rtpCapabilities,
    });

    if (producerTransportData.error) {
      console.error(producerTransportData.error);
      return;
    }
    if (process.env.REACT_APP_PRODUCER_SERVERS_URLS) {
      producerTransportData.iceServers = [
        {
          urls: process.env.REACT_APP_PRODUCER_SERVERS_URLS,
          username: process.env.REACT_APP_PRODUCER_SERVERS_USER_NAME,
          credential: process.env.REACT_APP_PRODUCER_SERVERS_USER_CREDENTIALS,
        },
      ];

      producerTransportData.iceTransportPolicy = 'relay';
    }

    producerTransportData['proprietaryConstraints'] = { optional: [{ googDscp: true }] };

    const producerTransport = device.createSendTransport(producerTransportData);

    console.info('07.4 producerTransportData ---->', {
      producerTransportId: producerTransport.id,
      producerTransportData: producerTransportData,
    });

    producerTransport.on('connect', async ({ dtlsParameters }, onSuccess, onError) => {
      try {
        await SocketService.request('connectTransport', {
          transport_id: producerTransport.id,
          dtlsParameters,
        });
        onSuccess();
      } catch (err) {
        onError();
      }
    });

    producerTransport.on('produce', async ({ kind, appData, rtpParameters }, onSuccess, onError) => {
      try {
        const { producer_id } = await SocketService.request('produce', {
          producerTransportId: producerTransport.id,
          kind,
          appData,
          rtpParameters,
          ...(appData.status !== undefined && { status: appData.status }),
        });
        onSuccess({
          id: producer_id,
        });
      } catch (err) {
        onError(err);
      }
    });

    producerTransport.on('connectionstatechange', (state) => {
      switch (state) {
        case 'connecting':
          break;
        case 'connected': {
          const isPipActive = useRoomActionsStore.getState().pipSettings.isActive;

          if (isPipActive) {
            useNotificationStore.getState().clearPersistentNotification();
          } else {
            useRoomStore.getState().setIsInfoModalOpen(false);
          }

          break;
        }
        case 'disconnected': {
          this.restartIce();
          if (useRoomStore.getState().infoModalContent.errorPriority !== 'HIGH') {
            const isPipActive = useRoomActionsStore.getState().pipSettings.isActive;

            if (isPipActive) {
              useNotificationStore.getState().setPersistentNotification({
                id: 'connection-issue',
                type: 'connection-issue',
                title: 'There was a connection issue',
                text: 'Reconnecting...',
              });
            } else {
              useRoomStore.getState().setInfoModalContent({
                icon: INFO_MODAL_ICONS.CONNECTION_ISSUE,
                title: 'There was a connection issue',
                footer: {
                  isLoading: true,
                  text: 'Reconnecting',
                },
                handleOnCloseModal: () => {
                  openURL('/rejoin');
                },
              });
              useRoomStore.getState().setIsInfoModalOpen(true);
            }
          }
          break;
        }
        case 'failed': {
          console.warn('producer failed', producerTransport);
          producerTransport.close();

          const isPipActive = useRoomActionsStore.getState().pipSettings.isActive;

          if (isPipActive) {
            useNotificationStore.getState().setPersistentNotification({
              id: 'connection-issue',
              type: 'connection-issue',
              title: 'There was a connection issue',
              text: 'Reconnecting...',
            });
          } else {
            useRoomStore.getState().setInfoModalContent({
              icon: INFO_MODAL_ICONS.WARNING,
              title: 'Oops! Something Went Wrong',
              description: 'Please try again later',
              errorPriority: 'HIGH',
              buttons: [
                {
                  text: 'Got it',
                  onClick: () => {
                    sendEventToWebView({ eventType: EWebViewEvents.WEB_SHEET_DISMISSED });
                    openURL('/rejoin');
                    useRoomStore.getState().setIsInfoModalOpen(false);
                  },
                  variant: 'primary',
                },
              ],
              handleOnCloseModal: () => {
                openURL('/rejoin');
              },
            });
            useRoomStore.getState().setIsInfoModalOpen(true);
          }

          break;
        }
        default:
          console.log('Producer transport connection state changes', {
            state: state,
            id: producerTransport.id,
          });
          break;
      }
    });

    producerTransport.on('icegatheringstatechange', (state) => {
      console.log('Producer icegatheringstatechange', {
        state: state,
        id: producerTransport.id,
      });
    });

    useProducersStore.setState({ producerTransport });

    // ####################################################
    // CONSUMER TRANSPORT
    // ####################################################

    const consumerTransportData = await SocketService.request('createWebRtcTransport', {
      forceTcp: false,
    });

    if (consumerTransportData.error) {
      console.error(consumerTransportData.error);
      return;
    }

    if (process.env.REACT_APP_CONSUMER_SERVERS_URLS) {
      consumerTransportData.iceServers = [
        {
          urls: process.env.REACT_APP_CONSUMER_SERVERS_URLS,
          username: process.env.REACT_APP_CONSUMER_SERVERS_USER_NAME,
          credential: process.env.REACT_APP_CONSUMER_SERVERS_USER_CREDENTIALS,
        },
      ];

      consumerTransportData.iceTransportPolicy = 'relay';
    }

    this.consumerTransport = device.createRecvTransport(consumerTransportData);

    console.info('07.5 consumerTransportData ---->', {
      consumerTransportId: this.consumerTransport.id,
      consumerTransportData: consumerTransportData,
    });

    this.consumerTransport.on('connect', async ({ dtlsParameters }, onSuccess, onError) => {
      try {
        await SocketService.request('connectTransport', {
          transport_id: this.consumerTransport.id,
          dtlsParameters,
        });
        onSuccess();
      } catch (err) {
        onError(err);
      }
    });

    this.consumerTransport.on('connectionstatechange', (state) => {
      switch (state) {
        case 'connecting':
          break;
        case 'connected': {
          console.log('Consumer Transport connected again');
          const isPipActive = useRoomActionsStore.getState().pipSettings.isActive;

          if (isPipActive) {
            useNotificationStore.getState().clearPersistentNotification();
          } else {
            useRoomStore.getState().setIsInfoModalOpen(false);
          }

          break;
        }
        case 'disconnected': {
          this.restartIce();
          if (useRoomStore.getState().infoModalContent.errorPriority !== 'HIGH') {
            const isPipActive = useRoomActionsStore.getState().pipSettings.isActive;

            if (isPipActive) {
              useNotificationStore.getState().setPersistentNotification({
                id: 'connection-issue',
                type: 'connection-issue',
                title: 'There was a connection issue',
                text: 'Reconnecting...',
              });
            } else {
              useRoomStore.getState().setInfoModalContent({
                icon: INFO_MODAL_ICONS.CONNECTION_ISSUE,
                title: 'There was a connection issue',
                footer: {
                  isLoading: true,
                  text: 'Reconnecting',
                },
                handleOnCloseModal: () => {
                  openURL('/rejoin');
                },
              });
              useRoomStore.getState().setIsInfoModalOpen(true);
            }
          }
          break;
        }
        case 'failed': {
          console.warn('Consumer Transport failed', { id: this.consumerTransport.id });
          this.consumerTransport.close();

          const isPipActive = useRoomActionsStore.getState().pipSettings.isActive;

          if (isPipActive) {
            useNotificationStore.getState().setPersistentNotification({
              id: 'connection-issue',
              type: 'connection-issue',
              title: 'There was a connection issue',
              text: 'Reconnecting...',
            });
          } else {
            useRoomStore.getState().setInfoModalContent({
              icon: INFO_MODAL_ICONS.WARNING,
              errorPriority: 'HIGH',
              title: 'Oops! Something Went Wrong',
              description: 'Please try again later',
              buttons: [
                {
                  text: 'Got it',
                  onClick: () => {
                    sendEventToWebView({ eventType: EWebViewEvents.WEB_SHEET_DISMISSED });
                    openURL('/rejoin');
                    useRoomStore.getState().setIsInfoModalOpen(false);
                  },
                  variant: 'primary',
                },
              ],
              handleOnCloseModal: () => {
                openURL('/rejoin');
              },
            });
            useRoomStore.getState().setIsInfoModalOpen(true);
          }

          break;
        }
        default:
          console.log('Consumer transport connection state changes', {
            state: state,
            id: this.consumerTransport.id,
          });
          break;
      }
    });

    this.consumerTransport.on('icegatheringstatechange', (state) => {
      console.log('Consumer icegatheringstatechange', {
        state: state,
        id: this.consumerTransport.id,
      });
    });
  }

  // ####################################################
  // RESTART ICE
  // ####################################################

  async restartIce() {
    try {
      if (useProducersStore.getState().producerTransport) {
        const iceParameters = await SocketService.request('restartIce', {
          transport_id: useProducersStore.getState().producerTransport.id,
        });

        await useProducersStore.getState().producerTransport.restartIce({ iceParameters });
      }

      if (this.consumerTransport) {
        const iceParameters = await SocketService.request('restartIce', {
          transport_id: this.consumerTransport.id,
        });

        await this.consumerTransport.restartIce({ iceParameters });
      }
    } catch (error) {
      console.error('Restart ICE error', error);
    }
  }

  // ####################################################
  // SOCKET ON
  // ####################################################

  initSockets() {
    SocketService.on(ECommonSocketKeys.CONNECT, this.handleSocketConnect);
    SocketService.on(ECommonSocketKeys.DISCONNECT, this.handleSocketDisconnect);

    // Store references for cleanup
    this.onOnline = () => {
      SocketService.connect();
    };
    this.onOffline = () => {
      SocketService.softDisconnect();
    };

    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
  }

  initTransportsSockets() {
    SocketService.on(ECommonSocketKeys.CONSUMER_CLOSED, this.handleConsumerClosed);
    SocketService.on(ECommonSocketKeys.SET_VIDEO_OFF, this.handleSetVideoOff);
    SocketService.on(ECommonSocketKeys.REFRESH_PARTICIPANTS_COUNT, this.handleRefreshParticipantsCount);
    SocketService.on(ECommonSocketKeys.NEW_PRODUCERS, this.handleNewProducers);
    SocketService.on(ECommonSocketKeys.UPDATE_PEER_INFO, this.handleUpdatePeerInfo);
    SocketService.on(ECommonSocketKeys.AUDIO_VOLUME, this.handleAudioVolumeData);
  }

  cleanup() {
    // Clear timers
    if (this._meetingEndSoonTimer) {
      clearTimeout(this._meetingEndSoonTimer);
      this._meetingEndSoonTimer = null;
    }

    // Remove window event listeners
    if (this.onOnline) {
      window.removeEventListener('online', this.onOnline);
      this.onOnline = null;
    }
    if (this.onOffline) {
      window.removeEventListener('offline', this.onOffline);
      this.onOffline = null;
    }

    // Clear all event listeners to prevent memory leaks
    if (this.eventListeners) {
      this.eventListeners.forEach((listeners, key) => {
        this.eventListeners.set(key, []);
      });
    }
  }

  // ####################################################
  // HANDLE SOCKET DATA
  // ####################################################

  handleConsumerClosed = ({ consumer_id, consumer_kind }) => {
    this.removeConsumer(consumer_id, consumer_kind);
  };

  handleSetVideoOff = (data) => {
    const { peer_id } = data;
    const isMe = peer_id === this.peer_id;

    if (isMe) {
      if (this.producerExist(EMediaType.screen)) {
        this.closeProducer(EMediaType.screen);
        useNotificationStore.getState().appendNotification({
          title: ' ',
          text: 'Your screen sharing was stopped by the host/co-host',
          source: 'share-screen',
          icon: 'share-screen',
        });
      }
      this.setVideoOff(data, false);
    } else {
      this.setVideoOff(data, true);
    }
  };

  handleRefreshParticipantsCount = (data) => {
    this.participantsCount = data.peer_counts;

    adaptAspectRatio(this.participantsCount);
  };

  handleNewProducers = async (data) => {
    if (!this.consumerTransport) return;
    if (data.length > 0) {
      for (let { producer_id, peer_name, peer_info, type } of data) {
        await this.consume(producer_id, peer_name, peer_info, type);
      }
    }
  };

  handleUpdatePeerInfo = (data) => {
    this.updatePeerInfo(data.peer_name, data.peer_id, data.type, data.status, false, data.peer_presenter);
    if (data.status === false && data.type === 'recording') {
      try {
        const myId = SocketService.getSocketId();
        if (data.peer_id === myId) {
          useNotificationStore.getState().appendNotification({
            title: 'Meeting Recording',
            text: 'The host stopped your recording session.',
            source: 'recording',
          });
        }
      } catch (e) {}
      const initiatorId = (data.extraData && data.extraData.initiator_peer_id) || undefined;
      this.stopRecording(false, initiatorId);
    }
  };

  handleAudioVolumeData = (data) => {
    this.handleAudioVolume(data);
  };

  handleSocketConnect = async () => {
    const socketId = SocketService.getSocketId();
    this.peer_id = socketId;

    const {
      roomId,
      infoModalContent: { errorPriority },
      peerName,
    } = useRoomStore.getState();

    if (errorPriority === 'HIGH') return;
    const myPeerInfo =
      usePeersStore.getState().getMyPeerInfo() ??
      generatePeerInfo({ peerName, peerImage: usePeersStore.getState().avatarUrl || '' });

    if (myPeerInfo.peer_id && roomId && peerName) {
      // Guard: prevent double join if connect fires again while we're already reconnecting
      if (this._isReconnecting) return;
      this._isReconnecting = true;
      try {
        cleanProducers(this.consumerTransport, { removeConnectionListeners: false });
        this.consumerTransport = null;
        await this.join({
          room_id: roomId,
          peer_info: { ...myPeerInfo, peer_id: socketId },
        });
      } finally {
        this._isReconnecting = false;
      }
    } else {
      this.refreshBrowser();
    }
  };

  handleSocketDisconnect = async () => {
    this.saveRecording('Socket disconnected');

    const isPipActive = useRoomActionsStore.getState().pipSettings.isActive;

    if (isPipActive) {
      useNotificationStore.getState().setPersistentNotification({
        id: 'connection-issue',
        type: 'connection-issue',
        title: 'There was a connection issue',
        text: 'Reconnecting...',
      });
    } else {
      useRoomStore.getState().setInfoModalContent({
        icon: INFO_MODAL_ICONS.CONNECTION_ISSUE,
        title: 'There was a connection issue',
        footer: {
          isLoading: true,
          text: 'Reconnecting',
        },
        handleOnCloseModal: () => {
          openURL('/rejoin');
        },
      });
      useRoomStore.getState().setIsInfoModalOpen(true);
    }
  };

  // ####################################################
  // SERVER AWAY/MAINTENANCE
  // ####################################################

  refreshBrowser() {
    exitRoomService({ offline: true, consumerTransport: this.consumerTransport });
    openURL(this.getReconnectDirectJoinURL());
  }

  getReconnectDirectJoinURL() {
    const { peer_token } = usePeersStore.getState().getMyPeerInfo() || {};
    const baseUrl = `${window.location.origin}/join`;
    const queryParams = {
      room: this.room_id,
    };
    if (peer_token) queryParams.token = peer_token;
    const url = `${baseUrl}?${Object.entries(queryParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')}`;
    return url;
  }

  // ####################################################
  // CHECK USER
  // ####################################################

  userNameAlreadyInRoom() {
    useRoomStore.setState({ isJoinedRoom: false });

    playSound('alert');
    Swal.fire({
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: localStorageSettings.swalBackground,
      imageUrl: image.user,
      position: 'center',
      title: 'Username',
      html: `The Username is already in use. <br/> Please try with another one`,
      showDenyButton: false,
      confirmButtonText: `OK`,
      showClass: { popup: 'animate__animated animate__fadeInDown' },
      hideClass: { popup: 'animate__animated animate__fadeOutUp' },
    }).then((result) => {
      if (result.isConfirmed) {
        openURL((window.location.href = '/join/?room=' + this.room_id));
      }
    });
  }

  // ####################################################
  // START LOCAL AUDIO VIDEO MEDIA
  // ####################################################

  async startLocalMedia() {
    const audioProducerExist = this.producerExist(EMediaType.audio);

    if (!useRoomActionsStore.getState().micSettings.isMicClosed) {
      if (!audioProducerExist) {
        await this.produce(
          EMediaType.audio,
          useMediaSettingsStore.getState().mediaSettings.selectedAudioInputDevice?.deviceId
        );
      }
    } else {
      if (useMediaSettingsStore.getState().isEnumerateAudioDevices && !audioProducerExist) {
        await this.produce(
          EMediaType.audio,
          useMediaSettingsStore.getState().mediaSettings.selectedAudioInputDevice.deviceId,
          false,
          false,
          false
        );
        await this.pauseAudioProducer();
      }
    }

    if (!useRoomActionsStore.getState().camSettings.isCamClosed) {
      await this.produce(
        EMediaType.video,
        useMediaSettingsStore.getState().mediaSettings.selectedVideoInputDevice.deviceId
      );
    } else {
      this.setVideoOff(usePeersStore.getState().getMyPeerInfo(), false);
      this.sendVideoOff();
      this.updatePeerInfo(this.peer_name, this.peer_id, 'video', false);
    }
  }

  async pauseAudioProducer() {
    this.setIsAudio(this.peer_id, false);
    await this.pauseProducer(EMediaType.audio);
    this.updatePeerInfo(this.peer_name, this.peer_id, 'audio', false);
    audioProcessor.setMuted(true); // Disconnect audio graph for zero CPU
  }

  // ####################################################
  // AUDIO/VIDEO/SCREEN CONSTRAINTS
  // ####################################################

  getAudioConstraints(deviceId) {
    // CONDITIONAL CONSTRAINTS BASED ON CURRENT PRESET
    const currentPreset = audioProcessor.currentPreset;

    let audio;

    switch (currentPreset) {
      case 'low':
        // LOW PRESET: Native browser processing only
        audio = {
          echoCancellation: true,
          noiseSuppression: true, // ENABLE native suppression for low preset
          autoGainControl: true, // Enable AGC for low preset
          channelCount: 1,
          sampleRate: 48000,
          latency: 0.02, // 20ms latency for native processing
        };
        break;

      case 'medium':
      case 'high':
        // HIGH PRESET: DeepFilterNet processing
        // MEDIUM PRESET: RNNoise processing
        {
          // Echo cancellation is dynamically controlled via audioProcessor toggle
          const useEchoCancellation = audioProcessor.echoCancellationEnabled;

          audio = {
            // Echo cancellation: OFF by default to avoid AEC+DeepFilter double processing
            // Enable when using speakers (not headphones) to prevent feedback
            echoCancellation: { exact: useEchoCancellation },
            noiseSuppression: { exact: false },
            autoGainControl: { exact: false },

            // Google/Chrome-specific constraints — sync echo with main toggle
            googEchoCancellation: useEchoCancellation,
            googAutoGainControl: false,
            googNoiseSuppression: false,
            googHighpassFilter: false,
            googTypingNoiseDetection: false,
            googArrayGeometry: false, // if using multi-mic arrays

            // Sample rate & channels
            sampleRate: { ideal: 48000 },
            channelCount: { ideal: 1 },
            latency: { ideal: 0.01 },
          };
        }
        break;

      default:
        // Fallback to safe defaults
        audio = {
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
          sampleRate: 48000,
        };
    }

    // Apply device ID if specified
    if (deviceId && deviceId !== 'default' && deviceId !== 'communications') {
      audio.deviceId = { exact: deviceId };
    } else if (deviceId === 'communications') {
      audio.deviceId = 'communications';
    }

    return {
      audio,
      video: false,
    };
  }

  getCameraConstraints() {
    this.camera = this.camera === 'user' ? 'environment' : 'user';
    if (this.camera !== 'user') this.camVideo = { facingMode: { exact: this.camera } };
    else this.camVideo = true;
    return {
      audio: false,
      video: this.camVideo,
    };
  }

  getVideoConstraints(deviceId) {
    // Get the user's selected video resolution from the store
    const selectedResolution = useMediaSettingsStore.getState().mediaSettings.selectedVideoResolution;

    // Delegate to centralized utility for consistent constraint building
    return getMediaSoupVideoConstraints(deviceId, selectedResolution);
  }

  // Quality fallback method for video constraints
  // Delegates to centralized utility for consistent behavior across the app
  async getUserMediaWithFallback(initialConstraints, deviceId) {
    // Get the user's selected video resolution from the store
    const selectedResolution = useMediaSettingsStore.getState().mediaSettings.selectedVideoResolution;

    // Delegate to centralized utility
    return getUserMediaWithFallbackUtil(deviceId, selectedResolution);
  }

  getScreenConstraints() {
    const frameRate = { ideal: 30 };

    // Base constraints structure with dynamic values for resolution and frame rate
    const screenBaseConstraints = (width, height) => ({
      audio: true,
      video: {
        width: { ideal: width },
        height: { ideal: height },
        aspectRatio: 1.777, // 16:9 aspect ratio
        frameRate,
      },
    });

    // Default to Full HD
    const { width, height } = { width: 1920, height: 1080 };

    return screenBaseConstraints(width, height);
  }

  // ####################################################
  // WEBCAM ENCODING
  // ####################################################

  getWebCamEncoding() {
    let encodings;

    const firstVideoCodec =
      this.device.rtpCapabilities.codecs.find(
        (c) =>
          c.kind === 'video' &&
          c.mimeType.toLowerCase() === 'video/h264' &&
          c.parameters['profile-level-id'] === '42e01f'
      ) || this.device.rtpCapabilities.codecs.find((c) => c.kind === 'video');

    // If VP9 is the only available video codec then use SVC.
    if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9') {
      encodings = [
        {
          maxBitrate: 2500000,
          scalabilityMode: 'L1T3',
        },
      ];
    } else {
      encodings = [
        {
          scaleResolutionDownBy: 1,
          maxBitrate: 2500000,
          scalabilityMode: 'L1T3',
        },
      ];

      encodings.unshift({
        scaleResolutionDownBy: 4,
        maxBitrate: 500000,
        scalabilityMode: 'L1T3',
      });
    }
    return { encodings, codec: firstVideoCodec };
  }

  // ####################################################
  // SCREEN ENCODING
  // ####################################################

  getScreenEncoding() {
    let encodings;

    const firstVideoCodec =
      this.device.rtpCapabilities.codecs.find(
        (c) =>
          c.kind === 'video' &&
          c.mimeType.toLowerCase() === 'video/h264' &&
          c.parameters['profile-level-id'] === '42e01f'
      ) || this.device.rtpCapabilities.codecs.find((c) => c.kind === 'video');

    // If VP9 is the only available video codec then use SVC.
    if (firstVideoCodec.mimeType.toLowerCase() === 'video/vp9') {
      encodings = [
        {
          maxBitrate: 5000000,
          scalabilityMode: 'L1T3',
          dtx: true,
        },
      ];
    } else {
      encodings = [
        {
          scaleResolutionDownBy: 1,
          maxBitrate: 5000000,
          scalabilityMode: 'L1T3',
          dtx: true,
        },
      ];
    }
    return { encodings, codec: firstVideoCodec };
  }

  // ####################################################
  // PRODUCER
  // ####################################################

  async produce(type, deviceId = null, swapCamera = false, init = false, status = undefined) {
    let mediaConstraints = {};
    let audio = false;
    let screen = false;
    const isSamsung = isSamsungDevice();

    if (type === EMediaType.video && this.isMobileDevice && isSamsung) {
      await forceCameraRelease();
    }

    switch (type) {
      case EMediaType.audio:
        mediaConstraints = this.getAudioConstraints(deviceId);
        audio = true;
        break;
      case EMediaType.video:
        swapCamera
          ? (mediaConstraints = this.getCameraConstraints())
          : (mediaConstraints = this.getVideoConstraints(deviceId));
        break;
      case EMediaType.screen:
        mediaConstraints = this.getScreenConstraints();
        screen = true;
        break;
      default:
        return;
    }
    if (!this.device.canProduce('video') && !audio) {
      return console.error('Cannot produce video');
    }
    if (useProducersStore.getState().producerLabel.has(type)) {
      return console.warn('Producer already exists for this type ' + type);
    }

    let stream;
    try {
      if (init) {
        stream = useVideoStreamStore.getState().originalStream;
      } else {
        if (screen) {
          stream = await navigator.mediaDevices.getDisplayMedia(mediaConstraints);
        } else {
          // For video, implement quality fallback mechanism
          if (type === EMediaType.video && !audio) {
            stream = await this.getUserMediaWithFallback(mediaConstraints, deviceId);
            // Store the ACTUAL stream (not a clone) so we can properly stop it later
            // The store's setOriginalStream will stop any previous stream before setting this one
            useVideoStreamStore.getState().setOriginalStream(stream);
          } else {
            stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
          }
        }
      }

      if (audio && !this.isMobileDevice) {
        stream = await applyNoiseCancelledStream(stream);
      } else if (audio) {
        stream = await applyNoiseCancellation(stream);
      }

      // For video, clone the track so the producer has its own independent track
      // This allows the original stream to be properly stopped when camera is closed
      // while the producer track can be replaced by VB without conflicts
      let track = audio
        ? stream.getAudioTracks()[0]
        : type === EMediaType.video
          ? stream.getVideoTracks()[0].clone()
          : stream.getVideoTracks()[0];

      // Virtual Background Enhancement:
      // If a background effect is active, ensure we use the modified stream
      // instead of the raw camera stream from the very first packet.
      if (!audio && !screen && type === EMediaType.video) {
        const { appliedEffect } = useVirtualBackgroundStore.getState();
        // Check availability of type 'none' is more robust than checking 'value'
        if (appliedEffect.type !== 'none') {
          let modifiedStream = useVideoStreamStore.getState().modifiedStream;

          // If not ready yet, wait briefly (up to 5000ms) to avoid sending raw frames
          if (!modifiedStream) {
            console.log('[RoomClient] Waiting for Virtual Background stream...');
            // Increase timeout to 5 seconds to accommodate slower devices/cold starts
            // This prevents "flash of original background" on slow initializations
            for (let i = 0; i < 50; i++) {
              await new Promise((r) => setTimeout(r, 100));
              modifiedStream = useVideoStreamStore.getState().modifiedStream;
              if (modifiedStream) break;
            }
          }

          if (modifiedStream) {
            const modifiedTrack = modifiedStream.getVideoTracks()[0];
            if (modifiedTrack && modifiedTrack.readyState === 'live') {
              console.log('[RoomClient] Using virtual background track for initial production');
              // CRITICAL: Stop the cloned track before switching to modifiedStream track
              // Otherwise the orphaned clone keeps the camera active
              if (track && track !== modifiedTrack) {
                track.stop();
              }
              track = modifiedTrack;
            }
          } else {
            console.warn('[RoomClient] Virtual background initialization timed out, falling back to original stream');
          }
        }
      }

      const params = {
        track,
        appData: {
          mediaType: type,
          ...(status !== undefined && { status }),
        },
      };

      if (audio) {
        // PRESET-SPECIFIC OPUS CODEC CONFIGURATION
        const currentPreset = audioProcessor.currentPreset;
        let codecOptions;

        switch (currentPreset) {
          case 'low':
            // LOW PRESET: Optimized for native browser processing
            codecOptions = {
              opusStereo: false,
              opusDtx: true, // Enable DTX for native processing
              opusFec: 1,
              opusNack: 1,
              opusMaxAverageBitrate: 64000, // Conservative bitrate for native
              opusMaxPlaybackRate: 48000,
              opusPacketLoss: 1,
            };
            break;

          case 'medium':
            // MEDIUM PRESET: Optimized for RNNoise
            codecOptions = {
              opusStereo: false,
              opusDtx: true,
              opusFec: 1,
              opusNack: 1,
              opusMaxAverageBitrate: 96000, // Higher bitrate for RNNoise processed audio
              opusMaxPlaybackRate: 48000,
              opusPacketLoss: 1,
            };
            break;

          case 'high':
            // HIGH PRESET: Optimized for DeepFilterNet
            codecOptions = {
              opusStereo: false,
              opusDtx: true,
              opusFec: 1,
              opusNack: 1,
              opusMaxAverageBitrate: 64000, // 64kbps: optimal for clean DeepFilter output (noise already removed)
              opusMaxPlaybackRate: 48000,
              opusPacketLoss: 10, // Higher FEC for better recovery on lossy networks
            };
            break;

          default:
            // Fallback configuration
            codecOptions = {
              opusStereo: false,
              opusDtx: true,
              opusFec: 1,
              opusNack: 1,
              opusMaxAverageBitrate: 96000,
              opusMaxPlaybackRate: 48000,
              opusPacketLoss: 1,
            };
        }

        params.codecOptions = codecOptions;
      }

      if (!audio && !screen) {
        const { encodings, codec } = this.getWebCamEncoding();

        params.encodings = encodings;
        params.codecs = codec;
        params.codecOptions = {
          videoGoogleStartBitrate: 1000,
        };
      }

      if (!audio && screen) {
        const { encodings, codec } = this.getScreenEncoding();

        params.encodings = encodings;
        params.codecs = codec;
        params.codecOptions = {
          videoGoogleStartBitrate: 1000,
        };
      }

      // Fix: If no audio producer exists, create a silent one first to establish transport connection
      // This ensures the transport is connected before attempting video/screen production
      if ((type === EMediaType.video || type === EMediaType.screen) && !init && !this.producerExist(EMediaType.audio)) {
        try {
          // Create silent audio stream using Web Audio API
          const audioContext = new AudioContext();
          const oscillator = audioContext.createOscillator();
          const dest = audioContext.createMediaStreamDestination();
          const gainNode = audioContext.createGain();
          gainNode.gain.value = 0; // Completely silent
          oscillator.connect(gainNode);
          gainNode.connect(dest);
          oscillator.start();

          // Create audio producer to establish transport connection
          await this.produce(EMediaType.audio, null, false, true, false);

          // Pause the silent audio producer immediately
          await this.pauseProducer(EMediaType.audio);

          // Cleanup audio context
          setTimeout(() => audioContext.close(), 1000);
        } catch (audioError) {
          console.error('Failed to create silent audio producer:', audioError);
        }
      }

      const producer = await useProducersStore.getState().producerTransport.produce(params);

      if (!producer) {
        throw new Error('Producer not found!');
      }

      useProducersStore.getState().appendProducer(producer.id, producer);
      useProducersStore.getState().appendProducerLabel(type, producer.id);

      // if screen sharing produce the tab audio + microphone
      if (screen && stream.getAudioTracks()[0]) {
        this.produceScreenAudio(stream);
      }

      if (!audio) {
        await this.handleProducer(producer.id, type, stream);
      } else {
        await this.handleProducer(producer.id, type, stream);
      }

      if (type === EMediaType.video) {
        usePeersStore.getState().updatePeerInfo(this.peer_id, { peer_video: true });
        this.handleHideMe();
        // Virtual Background Enhancement:
        // If a background effect is selected but not yet applied to the producer,
        // replace the track with the modified stream if available
        const { appliedEffect } = useVirtualBackgroundStore.getState();
        if (appliedEffect.value) {
          const modifiedStream = useVideoStreamStore.getState().modifiedStream;
          if (modifiedStream) {
            const modifiedTrack = modifiedStream.getVideoTracks()[0];
            if (modifiedTrack?.readyState === 'live') {
              console.log('[RoomClient] Replacing video track with virtual background effect');
              try {
                const oldTrack = producer.track;
                await producer.replaceTrack({ track: modifiedTrack });
                // Stop old track after successful replacement
                if (oldTrack && oldTrack !== modifiedTrack) {
                  oldTrack.stop();
                }
              } catch (error) {
                console.error('Error replacing track with virtual background:', error);
              }
            }
          }
        }
      }

      producer.on('trackended', async () => {
        await this.closeProducer(type);
      });

      producer.on('transportclose', async () => {
        if (!audio) {
          useVideoStreamStore.getState().removeVideo(producer.id);
        } else {
          useAudioStreamStore.getState().removeAudio(producer.id);
        }
        await this.closeProducer(type);
      });

      producer.on('close', async () => {
        if (!audio) {
          useVideoStreamStore.getState().removeVideo(producer.id);
        } else {
          useAudioStreamStore.getState().removeAudio(producer.id);
        }
        await this.closeProducer(type);
      });

      if (!init) {
        switch (type) {
          case EMediaType.audio:
            this.setIsAudio(this.peer_id, true);
            if (!!status) {
              this.event(_EVENTS.startAudio);
            }
            break;
          case EMediaType.video:
            this.setIsVideo(true);
            this.event(_EVENTS.startVideo);
            break;
          case EMediaType.screen:
            this.setIsScreen(true);
            this.event(_EVENTS.startScreen);
            break;
          default:
            break;
        }

        playSound('joined');
      }
      return producer;
    } catch (err) {
      console.error('Produce error:', err);
      handleCameraError(err, type);
    }
  }

  handleHideMe(isActive) {
    useVideoStreamStore.getState().updateVideo(this.peer_id, { isHideMe: isActive });
  }

  producerExist(type) {
    return useProducersStore.getState().producerLabel.has(type);
  }

  async handleProducer(id, type, stream) {
    switch (type) {
      case EMediaType.video:
      case EMediaType.screen:
        // let isScreen = type === mediaType.screen;
        type === EMediaType.video && this.removeVideoOff(this.peer_id);

        useVideoStreamStore.getState().appendVideo({
          id,
          // Current user video: always null - useVideoBox pulls from global originalStream/modifiedStream
          // This prevents flickering from dual-render (appendVideo then updateVideo)
          stream: type === EMediaType.video ? null : stream,
          peerId: this.peer_id,
          peerName: this.peer_name,
          type,
          volume: 0,
          audioVolume: 0,
          autoPlay: true,
          isPinned: type === EMediaType.screen,
          isPresenter: false,
          muted: true,
          poster: image.poster,
          isCurrentUser: true,
        });

        // For video, if a virtual background effect is selected, ensure it's applied
        if (type === EMediaType.video) {
          const { appliedEffect } = useVirtualBackgroundStore.getState();
          if (appliedEffect.value) {
            // Check if modified stream is available, if not use the original stream
            const modifiedStream = useVideoStreamStore.getState().modifiedStream;
            if (modifiedStream) {
              // Update the video stream with the modified stream that has the effect applied
              useVideoStreamStore
                .getState()
                .updateVideo(this.peer_id, { stream: modifiedStream }, undefined, 'videoType');
            } else {
              // If no modified stream is available, use the original stream
              useVideoStreamStore.getState().updateVideo(this.peer_id, { stream }, undefined, 'videoType');
            }
          } else {
            // If no effect is selected, use the original stream
            useVideoStreamStore.getState().updateVideo(this.peer_id, { stream }, undefined, 'videoType');
          }
        }

        type === EMediaType.video && this.checkPeerInfoStatus(usePeersStore.getState().getMyPeerInfo());

        break;
      case EMediaType.audio:
      case EMediaType.audioTab:
        const audioStream = await getMediaStreamSource(stream, 'audioType');
        if (!useAudioStreamStore.getState().audioStreams.find((audio) => audio.peerId === this.peer_id)) {
          useAudioStreamStore.getState().appendAudio({
            id,
            muted: true,
            autoPlay: true,
            volume: (usePeersStore.getState().getMyPeerInfo()?.peer_audio_volume ?? 0) / 100,
            peerId: this.peer_id,
            stream: audioStream,
            type: 'audioType',
            isCurrentUser: true,
          });
        } else {
          useAudioStreamStore.getState().appendAudio({
            id,
            muted: true,
            autoPlay: true,
            volume: (usePeersStore.getState().getMyPeerInfo()?.peer_audio_volume ?? 0) / 100,
            peerId: this.peer_id,
            stream: audioStream,
            type: 'audioType',
            isCurrentUser: true,
          });
        }
        useConsumersStore.getState().appendAudioConsumer(this.peer_id, id);
        break;
      default:
        break;
    }
  }

  async pauseProducer(type) {
    if (!useProducersStore.getState().producerLabel.has(type)) {
      return console.warn('There is no producer for this type ' + type);
    }

    const producer_id = useProducersStore.getState().producerLabel.get(type);
    useProducersStore.getState().producers.get(producer_id).pause();

    try {
      await SocketService.request('pauseProducer', { producer_id: producer_id });
    } catch (error) {
      console.error('Error pausing producer', error);
    }

    switch (type) {
      case EMediaType.audio:
        this.event(_EVENTS.pauseAudio);
        this.setIsAudio(this.peer_id, false);
        audioProcessor.setMuted(true); // Disconnect audio graph for zero CPU
        break;
      case EMediaType.video:
        this.event(_EVENTS.pauseVideo);
        break;
      case EMediaType.screen:
        this.event(_EVENTS.pauseScreen);
        break;
      default:
        return;
    }
  }

  async resumeProducer(type) {
    if (!useProducersStore.getState().producerLabel.has(type)) {
      return console.warn('There is no producer for this type ' + type);
    }

    const producer_id = useProducersStore.getState().producerLabel.get(type);
    const producer = useProducersStore.getState().producers.get(producer_id);

    // For video type, check if virtual background effect needs to be reapplied
    if (type === EMediaType.video) {
      const { appliedEffect } = useVirtualBackgroundStore.getState();
      if (appliedEffect.value) {
        const modifiedStream = useVideoStreamStore.getState().modifiedStream;
        if (modifiedStream) {
          const modifiedTrack = modifiedStream.getVideoTracks()[0];
          if (modifiedTrack?.readyState === 'live') {
            console.log('[RoomClient] Replacing video track with virtual background effect after resume');
            try {
              const oldTrack = producer.track;
              await producer.replaceTrack({ track: modifiedTrack });
              // Stop old track after successful replacement
              if (oldTrack && oldTrack !== modifiedTrack) {
                oldTrack.stop();
              }
            } catch (error) {
              console.error('Error replacing track with virtual background:', error);
            }
          }
        }
      }
    }

    producer.resume();

    try {
      await SocketService.request('resumeProducer', { producer_id: producer_id });
    } catch (error) {
      console.error('Error resuming producer', error);
    }

    switch (type) {
      case EMediaType.audio:
        this.event(_EVENTS.resumeAudio);
        this.setIsAudio(this.peer_id, true);
        audioProcessor.setMuted(false); // Reconnect audio graph
        break;
      case EMediaType.video:
        this.event(_EVENTS.resumeVideo);
        break;
      case EMediaType.screen:
        this.event(_EVENTS.resumeScreen);
        break;
      default:
        return;
    }
  }

  async closeProducer(type) {
    if (!useProducersStore.getState().producerLabel.has(type)) {
      return console.warn('There is no producer for this type ' + type);
    }

    const producer_id = useProducersStore.getState().producerLabel.get(type);

    const data = {
      peer_name: this.peer_name,
      producer_id: producer_id,
      type: type,
      status: false,
    };

    SocketService.emit('producerClosed', data);

    const producer = useProducersStore.getState().producers.get(producer_id);

    // CRITICAL: Stop the producer's track before closing
    // This ensures any cloned tracks release the camera resource
    if (producer?.track) {
      producer.track.stop();
    }

    producer?.close();
    useProducersStore.getState().deleteProducer(producer_id);
    useProducersStore.getState().producerLabel.delete(type);

    if (type !== EMediaType.audio) {
      useVideoStreamStore.getState().removeVideo(producer_id);
      if (type === EMediaType.video) {
        useVideoStreamStore.getState().setOriginalStream(null);
        useVideoStreamStore.getState().setModifiedStream(null);
      }
    }

    if (type === EMediaType.audio) {
      useAudioStreamStore.getState().removeAudio(producer_id);
    }

    switch (type) {
      case EMediaType.audio:
        // Clean up noise cancellation resources when audio producer is closed
        audioProcessor.setMuted(true); // Disconnect audio graph for zero CPU
        this.setIsAudio(this.peer_id, false);
        this.event(_EVENTS.stopAudio);
        break;
      case EMediaType.video:
        this.setIsVideo(false);
        this.event(_EVENTS.stopVideo);
        break;
      case EMediaType.screen:
        this.setIsScreen(false);
        this.event(_EVENTS.stopScreen);
        useMagicPenStore.getState().clearObjects();
        useMagicPenStore.getState().setAnnotatorIds([]);
        break;
      default:
        break;
    }

    playSound('left');
  }

  async produceScreenAudio(stream) {
    try {
      if (useProducersStore.getState().producerLabel.has(EMediaType.audioTab)) {
        return console.warn('Producer already exists for this type ' + EMediaType.audioTab);
      }

      const track = stream.getAudioTracks()[0];
      const params = {
        track,
        appData: {
          mediaType: EMediaType.audioTab,
        },
      };

      const producerSa = await useProducersStore.getState().producerTransport.produce({
        ...params,
        appData: { ...params.appData, status: !useRoomActionsStore.getState().micSettings.isMicClosed },
      });

      useProducersStore.getState().appendProducer(producerSa.id, producerSa);
      useProducersStore.getState().appendProducer(EMediaType.audioTab, producerSa.id);

      await this.handleProducer(producerSa.id, EMediaType.audioTab, stream);

      producerSa.on('trackended', async () => {
        await this.closeProducer(EMediaType.audioTab);
      });

      producerSa.on('transportclose', async () => {
        useAudioStreamStore.getState().removeAudio(producerSa.id);
        await this.closeProducer(EMediaType.audioTab);
      });

      producerSa.on('close', async () => {
        useAudioStreamStore.getState().removeAudio(producerSa.id);

        await this.closeProducer(EMediaType.audioTab);
      });
    } catch (err) {
      console.error('Produce error:', err);
    }
  }

  // ####################################################
  // CONSUMER
  // ####################################################

  async consume(producer_id, peer_name, peer_info, type) {
    const consumerState = useConsumersStore.getState();

    // Prevent duplicate consumers for the same producer_id
    if ([...consumerState.consumers.values()].some((c) => c.producerId === producer_id)) {
      console.warn(`Consumer for producer ${producer_id} already exists`);
      return;
    }

    try {
      this.wbUpdate(true);

      const { consumer, stream } = await this.getConsumeStream(producer_id, peer_info.peer_id, type);

      // Append consumer to store
      consumerState.appendConsumer(consumer.id, consumer);

      try {
        await SocketService.request('resumeConsumer', { consumer_id: consumer.id });
        // Do something with response if needed
      } catch (error) {
        console.error('Error resuming consumer', error);
        // You might want to cleanup here if resume fails
        await this.removeConsumer(consumer.id, consumer.kind);
        return;
      }

      consumer.on('trackended', () => {
        console.log('Consumer track ended', { id: consumer.id, type });
        this.removeConsumer(consumer.id, consumer.kind);
      });

      consumer.on('transportclose', () => {
        console.log('Consumer transport closed', { id: consumer.id, type });
        this.removeConsumer(consumer.id, consumer.kind);
      });

      this.handleConsumer(consumer.id, type, stream, peer_name, peer_info, consumer);
    } catch (error) {
      console.error('Error in consume', error);
    }
  }

  async getConsumeStream(producerId, peer_id, type) {
    const { rtpCapabilities } = this.device;

    const data = await SocketService.request('consume', {
      rtpCapabilities,
      consumerTransportId: this.consumerTransport.id,
      producerId,
    });

    const { id, kind, rtpParameters } = data;
    const codecOptions = {};
    const streamId = peer_id + (type === EMediaType.screen ? '-screen-sharing' : '-mic-webcam');
    const consumer = await this.consumerTransport.consume({
      id,
      producerId,
      kind,
      rtpParameters,
      codecOptions,
      streamId,
    });

    let stream = new MediaStream();
    stream.addTrack(consumer.track);

    return {
      consumer,
      stream,
      kind,
    };
  }

  async handleConsumer(id, type, stream, peer_name, peer_info = {}, consumer) {
    let elem;

    const remotePeerId = peer_info.peer_id;
    const remotePeerAudio = peer_info.peer_audio;
    const remotePeerAudioVolume = peer_info.peer_audio_volume;
    const remotePeerPresenter = peer_info.peer_presenter;

    switch (type) {
      case EMediaType.video:
      case EMediaType.screen:
        type === EMediaType.video && this.removeVideoOff(remotePeerId);
        // in iOS sometimes switching camera will emit a new producer -> new consumer id for the same peer + media type
        // so we need to remove the old video stream before appending the new one
        useVideoStreamStore.getState().removeVideoByPeerAndType(remotePeerId, type);
        // Pause the video consumer and make sure to resume it when tab is active and when the
        if (stream) consumer.pause();
        useVideoStreamStore.getState().appendVideo({
          id,
          stream,
          peerId: remotePeerId,
          peerName: peer_name,
          type,
          volume: 0,
          audioVolume: 0,
          autoPlay: true,
          isPinned: type === 'screenType',
          isPresenter: remotePeerPresenter,
          muted: true,
          poster: image.poster,
        });

        type === EMediaType.video && this.checkPeerInfoStatus(peer_info);

        if (isHideALLVideosActive) {
          isHideALLVideosActive = false;
          const btnsHA = document.querySelectorAll('.focusMode');
          btnsHA.forEach((btn) => {
            btn.style.color = 'white';
          });
        }

        this.setPeerAudio(remotePeerId, remotePeerAudio);

        playSound('joined');
        break;
      case EMediaType.audio:
        const audioStream = await getMediaStreamSource(stream, 'audioType');
        if (!useAudioStreamStore.getState().audioStreams.find((audio) => audio.peerId === remotePeerId)) {
          useAudioStreamStore.getState().appendAudio({
            id,
            autoPlay: true,
            muted: remotePeerAudioVolume === 0,
            volume: remotePeerAudioVolume / 100,
            peerId: remotePeerId,
            stream: audioStream,
            type: 'audioType',
          });
        } else {
          useAudioStreamStore.getState().updateAudio(remotePeerId, {
            id,
            autoPlay: true,
            muted: remotePeerAudioVolume === 0,
            volume: remotePeerAudioVolume / 100,
            peerId: remotePeerId,
            stream: audioStream,
            type: 'audioType',
          });
        }

        // Store audio consumer and set volume
        useConsumersStore.getState().appendAudioConsumer(remotePeerId, id);
        this.setPeerAudio(remotePeerId, remotePeerAudio);
        break;
      case EMediaType.audioTab:
        const screenAudioStreamConsumer = await getMediaStreamSource(stream, 'audioType');
        if (!useAudioStreamStore.getState().audioStreams.find((audio) => audio.id === id)) {
          useAudioStreamStore.getState().appendAudio({
            id,
            autoPlay: true,
            muted: false,
            volume: remotePeerAudioVolume / 100,
            peerId: remotePeerId,
            stream: screenAudioStreamConsumer,
            type: 'audioType',
          });
        }
        useConsumersStore.getState().appendAudioConsumer(remotePeerId, id);
        this.setPeerAudio(remotePeerId, remotePeerAudio);
        break;
      default:
        break;
    }
    return elem;
  }

  removeConsumer(consumer_id, consumer_kind) {
    const consumerState = useConsumersStore.getState();

    if (consumer_kind === 'video') {
      useVideoStreamStore.getState().removeVideo(consumer_id);
    }

    if (consumer_kind === 'audio') {
      const audioConsumerPlayerId = getMapKeyByValue(consumerState.audioConsumers, consumer_id);
      if (audioConsumerPlayerId) {
        consumerState.deleteAudioConsumer(audioConsumerPlayerId);
      } else {
        console.warn(`No audioConsumerPlayerId found for consumer_id: ${consumer_id}`);
      }
    }

    const consumer = consumerState.consumers.get(consumer_id);
    if (consumer) {
      if (typeof consumer.pause === 'function') {
        consumer.pause(); // Pause media before closing
      }
      consumer.close();
    } else {
      console.warn(`Consumer not found for id: ${consumer_id}`);
    }

    consumerState.deleteConsumer(consumer_id);
    playSound('left');
  }

  // ####################################################
  // HANDLE VIDEO OFF
  // ####################################################

  setVideoOff(peer_info = {}, remotePeer = false) {
    const { peer_id, peer_name, peer_audio, peer_presenter } = peer_info || {};

    this.removeVideoOff(peer_id);

    /* TODO: VIDEO_BOX_CONTAINER */
    useVideoStreamStore.getState().appendVideo({
      id: peer_id,
      stream: null,
      peerId: peer_id,
      peerName: peer_name,
      type: 'videoType',
      volume: 0,
      audioVolume: 0,
      autoPlay: true,
      isPresenter: peer_presenter,
      muted: true,
      poster: image.poster,
      isVideoOff: true,
      isCurrentUser: !remotePeer,
    });

    if (!remotePeer) {
      useVideoStreamStore.getState().setOriginalStream(null);
      useVideoStreamStore.getState().setModifiedStream(null);
    }

    this.checkPeerInfoStatus(peer_info);
    remotePeer ? this.setPeerAudio(peer_id, peer_audio) : this.setIsAudio(peer_id, peer_audio);
    this.wbUpdate(true);
    this.handleHideMe();
  }

  removeVideoOff(peer_id) {
    useVideoStreamStore.getState().removeVideo(peer_id);
    usePeersStore.getState().updatePeerInfo(peer_id, { peer_video: false });
  }

  // ####################################################
  // HELPERS
  // ####################################################

  event(evt) {
    if (this.eventListeners.has(evt)) {
      this.eventListeners.get(evt).forEach((callback) => callback());
    }
  }

  on(evt, callback) {
    this.eventListeners.get(evt).push(callback);
  }

  // ####################################################
  // SET
  // ####################################################

  setPeerAudio(peer_id, status) {
    usePeersStore.getState().updatePeerInfo(peer_id, { peer_audio: status });
    useAudioStreamStore.getState().updateAudio(peer_id, { muted: !status });
  }

  setIsAudio(peer_id, status) {
    usePeersStore.getState().updatePeerInfo(peer_id, { peer_audio: status });
    useAudioStreamStore.getState().updateAudio(peer_id, { muted: !status });
  }

  setIsVideo(status) {
    const currentVideoStatus = usePeersStore.getState().getMyPeerInfo()?.peer_video;
    if (currentVideoStatus === status) return;

    usePeersStore.getState().updatePeerInfo(this.peer_id, { peer_video: status });
    if (!usePeersStore.getState().getMyPeerInfo()?.peer_video) {
      this.setVideoOff(usePeersStore.getState().getMyPeerInfo(), false);
      this.sendVideoOff();
    }
  }

  setIsScreen(status) {
    usePeersStore.getState().updatePeerInfo(this.peer_id, { peer_screen: status });
    const myPeerInfo = usePeersStore.getState().getMyPeerInfo() || {};

    // If screen sharing is stopping, remove screen streams from video store
    if (!status) {
      // Remove screen streams for this peer
      useVideoStreamStore.getState().removeVideoByPeerAndType(this.peer_id, 'screenType');
    }

    // Only set video off if both screen and video are off
    if (!myPeerInfo.peer_screen && !myPeerInfo.peer_video) {
      this.setVideoOff(myPeerInfo, false);
      this.sendVideoOff();
    }
  }

  sendVideoOff() {
    SocketService.emit('setVideoOff', usePeersStore.getState().getMyPeerInfo() ?? {});
  }

  // ####################################################
  // GET
  // ####################################################

  isRecording() {
    return this._isRecording;
  }

  static get mediaType() {
    return EMediaType;
  }

  static get EVENTS() {
    return _EVENTS;
  }

  async getRoomInfo() {
    let room_info = await SocketService.request('getRoomInfo');
    return room_info;
  }

  refreshParticipantsCount() {
    SocketService.emit('refreshParticipantsCount');
  }

  // ####################################################
  // UTILITY
  // ####################################################

  thereAreParticipants() {
    if (useConsumersStore.getState().consumers.size > 0 || this.participantsCount > 1) {
      return true;
    }
    return false;
  }

  // ####################################################
  // RECORDING
  // ####################################################

  handleRecordingError(error, popupLog = true) {
    this.event(_EVENTS.stopRec);
  }

  getSupportedMimeTypes() {
    const possibleTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/mp4'];
    possibleTypes.splice(2, 0, 'video/mp4;codecs=h264,aac', 'video/webm;codecs=h264,opus');
    return possibleTypes.filter((mimeType) => {
      return MediaRecorder.isTypeSupported(mimeType);
    });
  }

  async startRecording() {
    // Initialize an empty array for recorded blobs
    recordedBlobs = [];

    try {
      // Get supported MIME types for MediaRecorder
      const supportedMimeTypes = this.getSupportedMimeTypes();

      // Validate supported MIME types
      if (!supportedMimeTypes || supportedMimeTypes.length === 0) {
        throw new Error('No supported MIME types available for recording.');
      }

      // Set recording options using the first supported MIME type
      const options = { mimeType: supportedMimeTypes[0] };

      // Request user permission for audio and video
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true, // Enable video recording
      });

      // Initialize MixedAudioRecorder
      this.audioRecorder = new MixedAudioRecorder(stream);

      // Retrieve audio streams from audio elements
      const audioStreams = this.getAudioStreamFromAudioElements();

      // Mix audio streams
      const audioMixerStreams = this.audioRecorder.getMixedAudioStream(
        audioStreams
          .getTracks()
          .filter((track) => track.kind === 'audio') // Process only audio tracks
          .map((track) => new MediaStream([track])) // Wrap each track in a MediaStream
      );

      const audioMixerTracks = audioMixerStreams.getTracks();

      // Combine video and mixed audio tracks
      const videoTracks = stream.getVideoTracks(); // Get video tracks from the original stream
      const finalStream = new MediaStream([...audioMixerTracks, ...videoTracks]);

      // Start recording based on the device type (mobile or desktop)
      if (this.isMobileDevice) {
        this.startMobileRecording(options, finalStream.getTracks());
      } else {
        this.recordingOptions(options, finalStream.getTracks());
      }
    } catch (error) {
      // Handle errors during the recording setup
      if (error.name === 'NotAllowedError') {
        // Pass the error to the handleMediaError function
        useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: false });
        this.stopRecording(true);
        this.isAudioEventListenerActive = false;
      }
    }
  }

  recordingOptions(options, audioMixerTracks) {
    useRecordingStore.setState({
      onConfirm: () => this.startDesktopRecording(options, audioMixerTracks),
      onDismiss: () => this.stopRecording(),
    });
  }

  startMobileRecording(options, audioMixerTracks) {
    try {
      // Combine audioMixerTracks and videoTracks into a single array
      const combinedTracks = [];

      if (Array.isArray(audioMixerTracks)) {
        combinedTracks.push(...audioMixerTracks);
      }

      const originalStream = useVideoStreamStore.getState().originalStream;
      if (originalStream !== null) {
        const videoTracks = originalStream.getVideoTracks();

        if (Array.isArray(videoTracks)) {
          combinedTracks.push(...videoTracks);
        }
      }

      const recCamStream = new MediaStream(combinedTracks);

      this.mediaRecorder = new MediaRecorder(recCamStream, options);

      this.initRecording();
    } catch (err) {
      this.handleRecordingError('Unable to record the camera + audio: ' + err, false);
    }
  }

  startDesktopRecording(options, audioMixerTracks) {
    // On desktop devices, record camera or screen/window... + all audio tracks
    const constraints = { video: true };
    navigator.mediaDevices
      .getDisplayMedia(constraints)
      .then((screenStream) => {
        const screenTracks = screenStream.getVideoTracks();

        const combinedTracks = [];

        if (Array.isArray(screenTracks)) {
          combinedTracks.push(...screenTracks);
        }
        if (Array.isArray(audioMixerTracks)) {
          combinedTracks.push(...audioMixerTracks);
        }

        const recScreenStream = new MediaStream(combinedTracks);

        this.recScreenStream = recScreenStream;
        this.mediaRecorder = new MediaRecorder(recScreenStream, options);
        this.initRecording();
      })
      .catch((err) => {
        this.handleRecordingError('Unable to record the screen + audio: ' + err, false);
      });
  }

  initRecording() {
    this._isRecording = true;

    try {
      this.handleMediaRecorder();
    } catch (error) {
      console.error('handleMediaRecorder failed:', error);
    }
    this.event(_EVENTS.startRec);
    this.recordingAction('Start Meeting recording');
    this.updatePeerInfo(this.peer_name, this.peer_id, 'recording', true);
  }

  getAudioStreamFromAudioElements() {
    const audioElements = useAudioStreamStore.getState().audioStreams;
    const audioStream = new MediaStream();
    audioElements.forEach(({ stream }) => {
      // Exclude avatar Preview Audio
      if (stream.id !== 'avatarPreviewAudio') {
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          audioStream.addTrack(audioTrack);
        }
      }
    });
    return audioStream;
  }

  handleMediaRecorder() {
    if (this.mediaRecorder) {
      this.mediaRecorder.addEventListener('dataavailable', (evt) => this.handleMediaRecorderData(evt));
      this.mediaRecorder.addEventListener('stop', (evt) => this.handleMediaRecorderStop(evt));
      this.mediaRecorder.addEventListener('inactive', (evt) => this.handleMediaRecorderStop(evt));

      // Catch Firefox errors when starting MediaRecorder
      try {
        this.mediaRecorder.start();
      } catch (startError) {
        console.error('MediaRecorder.start() failed:', startError);
      }

      // Add listener for screen share ending
      if (this.recScreenStream) {
        const screenTrack = this.recScreenStream.getVideoTracks()?.[0];
        if (screenTrack) {
          screenTrack.addEventListener('ended', (evt) => this.handleMediaRecorderStop(evt));
        }
      }
    }
  }

  handleMediaRecorderData(evt) {
    if (evt.data && evt.data.size > 0) {
      recordedBlobs.push(evt.data);
    }
  }

  handleMediaRecorderStop(evt) {
    try {
      this.handleLocalRecordingStop(evt);
    } catch (err) {
      console.error('Recording save failed', err);
    }
    this.event(_EVENTS.stopRec);
  }

  handleLocalRecordingStop(evt) {
    if (this.isAudioEventListenerActive) {
      const dateTime = getDataTimeString();
      const type = recordedBlobs[0]?.type?.includes('mp4') ? 'mp4' : 'webm';
      const blob = new Blob(recordedBlobs, { type: 'video/' + type });
      const recFileName = `Rec_${dateTime}.${type}`;
      this.saveRecordingInLocalDevice(blob, recFileName, evt.type);
    }
  }

  saveRecordingInLocalDevice(blob, recFileName, eventType) {
    if (eventType === 'stop') {
      const url = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement('a');
      downloadLink.style.display = 'none';
      downloadLink.href = url;
      downloadLink.download = recFileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();

      setTimeout(() => {
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(url);
        recordedBlobs = [];
      }, 100);
    }
    this.stopRecording();
  }

  stopRecording(skipPeerInfoUpdate = false, initiatorPeerId = undefined) {
    if (this.mediaRecorder) {
      this._isRecording = false;
      this.mediaRecorder.stop();
      this.mediaRecorder = null;
      if (this.recScreenStream) {
        this.recScreenStream.getTracks().forEach((track) => {
          if (track.kind === 'video') track.stop();
        });
      }
      this.audioRecorder.stopMixedAudioStream();
      if (skipPeerInfoUpdate) return;
      this._recordingActionInitiatorId = initiatorPeerId;
      this.recordingAction('Stop conference recording');
      this.updatePeerInfo(this.peer_name, this.peer_id, 'recording', false);
      useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: false });
    }
    this.event(_EVENTS.stopRec); // in case of dismiss mediaRecorder is not created yet
  }

  recordingAction(action) {
    if (!this.thereAreParticipants()) return;
    SocketService.emit(ECommonSocketKeys.RECORDING_ACTION, {
      peer_name: this.peer_name,
      peer_id: this.peer_id,
      action,
      status: this._isRecording,
      initiator_peer_id: this._recordingActionInitiatorId || this.peer_id,
    });
    this._recordingActionInitiatorId = undefined;
  }

  saveRecording(reason) {
    if (this._isRecording) {
      this.stopRecording();
    }
  }

  // ####################################################
  // HANDLE AUDIO VOLUME
  // ####################################################

  handleAudioVolume(data) {
    const { peer_id, audioVolume } = data;
    useVideoStreamStore.getState().updateVideo(peer_id, { audioVolume });
    const peer = usePeersStore.getState().peers.get(peer_id);
    if (!peer?.peer_info?.peer_audio && audioVolume > 1) {
      usePeersStore.getState().updatePeerInfo(peer_id, { ...{ peer_audio: true } });
    }
  }

  // ####################################################
  // UPDATE PEER INFO
  // ####################################################

  updatePeerInfo(peer_name, peer_id, type, status, emit = true, presenter = false) {
    this.isAudioEventListenerActive = true;
    if (emit) {
      switch (type) {
        case 'audio':
          this.setIsAudio(peer_id, status);
          break;
        case 'video':
          this.setIsVideo(status);
          break;
        case 'screen':
          this.setIsScreen(status);
          break;
        case 'hand':
          status ? usePeersStore.getState().raiseHand(peer_id) : usePeersStore.getState().lowerHand(peer_id);
          if (status) {
            this.event(_EVENTS.raiseHand);
            // No need to play sound for who is raising hand just notify others
          } else {
            this.event(_EVENTS.lowerHand);
          }
          break;
        case 'recording':
          usePeersStore.getState().updatePeerInfo(peer_id, { peer_recording: status });
          break;

        default:
          break;
      }
      const data = {
        room_id: this.room_id,
        peer_name: peer_name,
        peer_id: peer_id,
        type: type,
        status: status,
        broadcast: true,
      };
      SocketService.emit('updatePeerInfo', data, (response) => {
        if (response?.error) {
          if (type === 'recording') {
            this.isAudioEventListenerActive = false;
            this.stopRecording(true);
            this._isRecording = false;
            useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: true });
          }
        }
      });
    } else {
      switch (type) {
        case 'audio':
          this.setPeerAudio(peer_id, status);
          break;
        case 'video':
          break;
        case 'screen':
          break;
        case 'hand':
          status ? usePeersStore.getState().raiseHand(peer_id) : usePeersStore.getState().lowerHand(peer_id);
          this.peer_id === peer_id && useRoomActionsStore.getState().setRaiseHandSettings({ isActive: status });
          if (status) {
            //trigger the raise hand notification
            useNotificationStore.getState().addRaisedHandNotification(peer_id, peer_name);
            playSound('raiseHand', true);
          }
          break;
        default:
          break;
      }
    }
  }

  // ####################################################

  roomAction(action, emit = true, popup = true) {
    const data = {
      room_id: this.room_id,
      peer_id: this.peer_id,
      peer_name: this.peer_name,
      peer_uuid: this.peer_uuid,
      action: action,
      password: null,
    };
    if (emit) {
      switch (action) {
        case 'broadcasting':
          SocketService.emit('roomAction', data);
          break;
        case 'isBanned':
          SocketService.emit('roomAction', data);
          this.isBanned();
          break;
        default:
          break;
      }
    }
  }

  checkPeerInfoStatus(peer_info = {}) {
    const peer_id = peer_info.peer_id;
    const peer_hand_status = peer_info.peer_hand;
    usePeersStore.getState().updatePeerInfo(peer_id, { ...peer_info });
    !!peer_hand_status ? usePeersStore.getState().raiseHand(peer_id) : usePeersStore.getState().lowerHand(peer_id);
  }

  // ####################################################
  // ATOMIC CAMERA OPERATIONS - PREVENTS ALL DUPLICATES
  // ####################################################

  async atomicCameraOperation(operation) {
    // Return immediately if same operation is already in progress
    if (this.cameraOperationLock) {
      return this.cameraOperationLock;
    }

    // Create new operation promise
    this.cameraOperationLock = new Promise(async (resolve, reject) => {
      try {
        const result = await operation();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        this.cameraOperationLock = null;
      }
    });
    return this.cameraOperationLock;
  }

  async atomicCloseProducer(type) {
    if (type !== 'videoType') {
      return this.closeProducer(type);
    }

    return this.atomicCameraOperation(async () => {
      return this.closeProducer(type);
    });
  }

  async atomicProduce(type, deviceId = null, swapCamera = false, init = false) {
    if (type !== 'videoType') {
      return this.produce(type, deviceId, swapCamera, init);
    }

    return this.atomicCameraOperation(async () => {
      // Before producing, ensure no duplicate producer exists
      if (useProducersStore.getState().producerLabel.has(type)) {
        await this.closeProducer(type);
        // Small delay to ensure cleanup
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      return this.produce(type, deviceId, swapCamera, init);
    });
  }

  async atomicCloseThenProduce(type, deviceId = null, swapCamera = false) {
    if (type !== 'videoType') {
      await this.closeProducer(type);
      setTimeout(async () => {
        try {
          await this.produce(type, deviceId, swapCamera);
        } catch (error) {
          console.error('Error in non-atomic closeThenProduce:', error);
        }
      }, 500);
      return;
    }

    return this.atomicCameraOperation(async () => {
      await this.closeProducer(type);
      await new Promise((resolve) => setTimeout(resolve, 500));
      return this.produce(type, deviceId, swapCamera);
    });
  }
} // End

export { RoomClient };
